'use client';

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useRouter } from 'next/navigation';

interface User {
  id: string;
  email: string;
  name: string;
  // Support both camelCase (frontend) and snake_case (backend) conventions
  merchantId?: string;
  merchant_id?: string;
  role?: string;
  businessName?: string;
}

interface AuthContextType {
  user: User | null;
  /** True only during the one-time mount auth check — used by route guards */
  isInitializing: boolean;
  /** @deprecated use isInitializing for guards; login/register have their own local loading state */
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginPublic: (email: string, password: string) => Promise<void>;
  registerMerchant: (businessName: string, firstName: string, lastName: string, email: string, phone: string, password: string) => Promise<{ requiresVerification?: boolean; email?: string }>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to get cookie value
function getCookie(name: string): string | null {
  if (typeof window === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

// Helper function to set cookie
function setCookie(name: string, value: string, days: number = 7) {
  if (typeof window === 'undefined') return;
  const maxAge = days * 24 * 60 * 60;
  const isSecure = window.location.protocol === 'https:';
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax${isSecure ? '; Secure' : ''}`;
}

// Helper function to delete cookie
function deleteCookie(name: string) {
  if (typeof window === 'undefined') return;
  document.cookie = `${name}=; path=/; max-age=0`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // isInitializing: true only while the one-time mount checkAuth() is in-flight
  const [isInitializing, setIsInitializing] = useState(true);
  // isLoading: true during login/register/logout API calls (drives button spinners)
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = getCookie('auth_token') || sessionStorage.getItem('auth_token');

      if (!token) {
        setUser(null);
        return;
      }

      // Validate token against the server on every mount/refresh.
      // This handles: new tabs (empty sessionStorage), expired tokens, and stale user data.
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/api/v1';
      const response = await fetch(`${apiUrl}/account/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Token is invalid or expired — clear all auth state
        deleteCookie('auth_token');
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('user_data');
        sessionStorage.removeItem('id');
        sessionStorage.removeItem('session_secret');
        setUser(null);
        return;
      }

      const data = await response.json();

      if (data.success && data.data) {
        const profile = data.data;

        const user: User = {
          id: profile.id,
          email: profile.email,
          name: profile.name || `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim(),
          merchantId: profile.merchant_id,
          merchant_id: profile.merchant_id,
          role: profile.role,
          businessName: profile.business_name,
        };

        // Repopulate sessionStorage with fresh data (covers new-tab scenario)
        sessionStorage.setItem('user_data', JSON.stringify(user));
        sessionStorage.setItem('id', user.id);
        sessionStorage.setItem('auth_token', token);

        setUser(user);
      } else {
        setUser(null);
      }
    } catch (error) {
      // Network error — fall back to cached sessionStorage data so a brief
      // connectivity blip doesn't log the user out
      console.error('Auth check failed:', error);
      const cachedUserData = sessionStorage.getItem('user_data');
      const cachedToken = getCookie('auth_token') || sessionStorage.getItem('auth_token');
      if (cachedUserData && cachedToken) {
        setUser(JSON.parse(cachedUserData));
      } else {
        setUser(null);
      }
    } finally {
      setIsInitializing(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      // Use the Go-API /authenticate endpoint with JWT
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/api/v1'}/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'login',
          email,
          password
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        // Check if this is an email_not_verified error
        if (data.error === 'email_not_verified') {
          // Store email for verification page
          sessionStorage.setItem('pending_verification_email', email);
          sessionStorage.setItem('pending_verification_password', password);
          // Throw a special error with the email
          const error = new Error(data.message || 'Please verify your email first') as any;
          error.emailNotVerified = true;
          error.email = email;
          throw error;
        }
        // Bubble up the real backend error message (e.g. "Invalid credentials")
        throw new Error(data.message || data.error || 'Login failed');
      }

      const data = await response.json();

      // Check for email_not_verified error in successful response
      if (data.error === 'email_not_verified') {
        sessionStorage.setItem('pending_verification_email', email);
        sessionStorage.setItem('pending_verification_password', password);
        const error = new Error(data.message || 'Please verify your email first') as any;
        error.emailNotVerified = true;
        error.email = email;
        throw error;
      }

      // Go-API returns data directly at root level: {token, user, merchant, session_secret, ...}
      if (data.token && data.user) {
        const { token, user, session_secret } = data;

        // Store JWT token in both cookie and sessionStorage (new auth method)
        setCookie('auth_token', token, 7);
        sessionStorage.setItem('auth_token', token);

        // Store legacy keys for backward compatibility
        sessionStorage.setItem('id', user.id);
        if (session_secret) {
          sessionStorage.setItem('session_secret', session_secret);
        }

        // Store user data
        sessionStorage.setItem('user_data', JSON.stringify(user));

        setUser(user);

        // Redirect to dashboard
        router.push('/dashboard');
      } else {
        throw new Error(data.message || data.error || 'Login failed');
      }
    } catch (error) {
      throw error;
    }
  };

  const loginPublic = async (email: string, password: string) => {
    try {
      // Use the Go-API public login endpoint with JWT
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/api/v1'}/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'login',
          email,
          password
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || errData.error || 'Login failed');
      }

      const data = await response.json();

      // Go-API returns data directly at root level: {token, user, merchant, session_secret, ...}
      if (data.token && data.user) {
        const { token, user, session_secret } = data;

        // Store JWT token in both cookie and sessionStorage (new auth method)
        setCookie('auth_token', token, 7);
        sessionStorage.setItem('auth_token', token);

        // Store legacy keys for backward compatibility
        sessionStorage.setItem('id', user.id);
        if (session_secret) {
          sessionStorage.setItem('session_secret', session_secret);
        }

        // Store user data
        sessionStorage.setItem('user_data', JSON.stringify(user));

        setUser(user);

        // Redirect to dashboard
        router.push('/dashboard');
      } else {
        throw new Error(data.message || data.error || 'Login failed');
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    // Clear all auth data from cookie and sessionStorage
    deleteCookie('auth_token');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('user_data');
    sessionStorage.removeItem('id');
    sessionStorage.removeItem('session_secret');

    setUser(null);
    router.push('/login');
  };

  const registerMerchant = async (businessName: string, firstName: string, lastName: string, email: string, phone: string, password: string) => {
    try {
      // Use the Go-API /authenticate endpoint for registration
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/api/v1'}/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'register',
          business_name: businessName,
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          password
        }),
      });

      if (!response.ok) {
        throw new Error('Registration failed');
      }

      const data = await response.json();

      // Go-API returns data directly at root level: {user, merchant, ...}
      // Note: Registration now does NOT return token - user must verify email first
      if (data.user && data.merchant) {
        // User needs to verify email - don't create session
        return {
          requiresVerification: true,
          email: email
        };
      } else if (data.token && data.user) {
        const { token, user, session_secret } = data;

        setCookie('auth_token', token, 7);
        sessionStorage.setItem('auth_token', token);
        sessionStorage.setItem('id', user.id);
        if (session_secret) {
          sessionStorage.setItem('session_secret', session_secret);
        }
        sessionStorage.setItem('user_data', JSON.stringify(user));
        setUser(user);

        return {
          requiresVerification: false
        };
      } else {
        throw new Error(data.message || 'Registration failed');
      }
    } catch (error) {
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, isInitializing, isLoading, login, loginPublic, registerMerchant, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  // Add helper to get merchant ID regardless of field naming convention
  // Use useCallback to make it stable
  const getMerchantId = useCallback(() => {
    if (!context.user) return null;
    return context.user.merchantId || context.user.merchant_id || null;
  }, [context.user]);

  return {
    ...context,
    getMerchantId
  };
}